// Thresholds shared between the reconciliation logic below and the
// components that actively re-check an outstanding optimistic guess
// (DetailsBox.vue, Orders.vue). Orders go through an SQS-backed consumer
// that writes to FileMaker asynchronously and can legitimately take a
// while (see server/sqs-queue), and the FM database is a shared,
// sometimes-taxed resource - so these are deliberately spaced out in
// minutes rather than seconds. Re-checking faster couldn't resolve any
// sooner than the backend actually writes, and would just add load.
export const OPTIMISTIC_POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 min between active re-checks
export const OPTIMISTIC_MAX_POLL_ATTEMPTS = 6; // stop actively polling after ~18 min
export const OPTIMISTIC_MAX_AGE_MS = 30 * 60 * 1000; // drop the guess entirely after 30 min

// Set orders don't get a workOrderId until they're fully processed, so
// there's no real id to match an optimistic entry against a freshly-written
// record. Two Set orders for the same project/lot placed around the same
// time are still indistinguishable without a server-issued correlation id,
// so this is necessarily a heuristic - good enough to reconcile the common
// "my own optimistic entry vs. the record that was just written for it"
// case without one.
function setOrderSignature(order) {
  return [
    order && order.project && order.project.projectId,
    order && order.subProject && order.subProject.name,
    order && order.tripType,
  ].join('|');
}

const orders = {
  namespaced: true,
  state: {
    displayOrderType: 'Pending',
    // Orders placed successfully but not yet written to FileMaker by the SQS
    // consumer. Each entry is `{ ...dispatchSetShape, _optimisticKey,
    // _optimisticAt, _isStaged? }`. Service orders (pull/respot/roundtrip)
    // carry a real workOrderId and are removed once a matching real order is
    // reconciled; set orders have no workOrderId yet and are matched by
    // project/lot/tripType instead (see reconcileOptimisticOrders). Service
    // orders also get `_isStaged: true` once a cheap per-order probe confirms
    // the record exists in FileMaker's staging table (see
    // markOptimisticOrderStaged) - that resolves the "did my submission even
    // go through" uncertainty well before the box's DispatchSet necessarily
    // shows up as Pending, which is a separate, slower dispatch-side step.
    optimisticOrders: [],
    // Optimistic trip types for individual boxes, keyed by workOrderId.
    // Persists across navigation so the box details view can render pending
    // state even after navigating away and back.
    optimisticBoxStates: {},
  },
  mutations: {
    setDisplayOrderType(state, { display }) {
      state.displayOrderType = display;
    },
    addOptimisticOrder(state, order) {
      if (!order) {
        return;
      }
      // Avoid duplicate optimistic entries for the same service order.
      const key = order._optimisticKey;
      if (key && state.optimisticOrders.some(o => o._optimisticKey === key)) {
        return;
      }
      state.optimisticOrders.push(order);
    },
    // Drop optimistic orders once a matching real order is present. Service
    // orders match by workOrderId. Set orders have no workOrderId yet, so
    // they're matched by project + lot + trip type instead - without this
    // they could never be reconciled by identity and would show as a
    // duplicate alongside the real order once it landed.
    reconcileOptimisticOrders(state, realOrders = []) {
      const realIds = new Set(
        realOrders.map(o => o && o.workOrderId).filter(Boolean)
      );
      const realSetSignatures = new Set(
        realOrders
          .filter(o => o && o.tripType === 'Set')
          .map(o => setOrderSignature(o))
      );
      state.optimisticOrders = state.optimisticOrders.filter(o => {
        if (!o) {
          return false;
        }
        if (o.workOrderId) {
          return !realIds.has(o.workOrderId);
        }
        if (o.tripType === 'Set') {
          return !realSetSignatures.has(setOrderSignature(o));
        }
        return true;
      });
    },
    clearOptimisticOrders(state) {
      state.optimisticOrders = [];
    },
    // Mark an optimistic service order as confirmed present in FileMaker's
    // staging table. This is intentionally independent of
    // reconcileOptimisticOrders: the staging record can exist well before
    // the box's DispatchSet transitions to a Pending status (that's a
    // separate dispatch-side step), so callers use this to stop implying
    // the submission itself is still unconfirmed without prematurely
    // removing the placeholder from the list.
    markOptimisticOrderStaged(state, workOrderId) {
      if (!workOrderId) {
        return;
      }
      state.optimisticOrders = state.optimisticOrders.map(o => {
        if (o && o.workOrderId === workOrderId && !o._isStaged) {
          return { ...o, _isStaged: true };
        }
        return o;
      });
    },
    // Set optimistic trip type for a specific box. Persists across navigation.
    setOptimisticBoxState(state, { workOrderId, tripType }) {
      if (!workOrderId) {
        return;
      }
      state.optimisticBoxStates = {
        ...state.optimisticBoxStates,
        [workOrderId]: {
          tripType,
          setAt: Date.now(),
        },
      };
    },
    // Clear optimistic state for a box once real data arrives.
    clearOptimisticBoxState(state, workOrderId) {
      if (!workOrderId || !state.optimisticBoxStates[workOrderId]) {
        return;
      }
      const updated = { ...state.optimisticBoxStates };
      delete updated[workOrderId];
      state.optimisticBoxStates = updated;
    },
  },
};

export default orders;
